import { TFunction } from 'i18next';
import { useEffect, useMemo, useRef } from 'react';
import { FieldValues, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { composeRules } from '../rules/fieldRule';
import {
  FieldEl,
  I18nMessage,
  RuleContext,
  UseFieldDeps,
  UseFieldFn,
  UseFieldOptions,
} from '../type';
import { evaluateCondition } from '../utils/condition';
import { useFieldContext } from './useFieldContext';

/**
 * 다국어 메시지 변환 함수
 * @param t 다국어 변환 함수 t
 * @param message 다국어 메시지 object
 * @returns 변환된 다국어 메시지 문자열
 */
const getI18nMessage = (t: TFunction, message: I18nMessage) => {
  if (typeof message === 'string') {
    return t(message as string);
  }
  const { key, values } = message;
  return t(key as any, { ...values });
};

const normalizePath = (p?: string) => p?.replace(/\[(\d+)\]/g, '.$1');

const isSameOrParentPath = (active?: string, name?: string) => {
  if (!active || !name) {
    return false;
  }

  const a = normalizePath(active);
  const n = normalizePath(name);
  return a === n || n?.startsWith(`${a}.`);
};

type FieldSetter = (value: string) => void;

const fieldSetterMap = new WeakMap<HTMLInputElement, FieldSetter>();

export const setFieldValueByElement = (el: HTMLInputElement, value: string) => {
  fieldSetterMap.get(el)?.(value);
};

/**
 *
 * @param form
 * @returns
 */
export function createUseField<TValues extends FieldValues>(
  form: UseFieldDeps<TValues>,
  setFieldRef: (name: Path<TValues>, el: FieldEl) => void,
) {
  return function useField<TName extends Path<TValues>>(
    name: TName,
    options?: UseFieldOptions<TValues>,
  ) {
    const { t } = useTranslation();

    const ctx = useFieldContext<TValues>(form);

    const wasInvalidRef = useRef<boolean>(false);

    const readOnlyResolved = useMemo(() => {
      return evaluateCondition(options?.readOnly ?? false, ctx);
    }, [options?.readOnly, ctx]);

    const disabledResolved = useMemo(() => {
      return evaluateCondition(options?.disabled ?? false, ctx);
    }, [options?.disabled, ctx]);

    const visibleResolved = useMemo(() => {
      return evaluateCondition(options?.visible ?? true, ctx);
    }, [options?.visible, ctx]);

    const readOnlyRef = useRef(readOnlyResolved);
    const disabledRef = useRef(disabledResolved);
    const visibleRef = useRef(visibleResolved);

    /**
     * 필드에 선언된 검증 룰 수행
     */
    const runValidate = useMemo(() => {
      const rs = options?.rules ?? [];
      return rs.length ? composeRules<TValues>(...rs) : null;
    }, [options?.rules]);

    /**
     * 룰에 설정된 디펜던시 필드 목록을 가져온다.
     */
    // const depNames = useMemo(() => {
    //   const rs = options?.rules ?? [];
    //   const deps = rs.flatMap((r) => r.deps ?? []);
    //   return Array.from(new Set(deps));
    // }, [options?.rules]);

    // const depValues = form.watch(depNames as any);
    const composingRef = useRef<boolean>(false);

    const { field, fieldState } = useController({
      name,
      control: form.control,
      rules: runValidate
        ? {
            validate: (value: any) => {
              const ctx: RuleContext<TValues> = {
                name: String(name),
                values: form.getValues() as TValues,
                phase: 'change' as const,
              };
              const res = runValidate(String(value ?? ''), ctx);

              if (res === true) {
                //activeErrorField 초기화 처리
                if (form.activeErrorField === String(name)) {
                  form.setActiveErrorField('');
                }
              }

              if (!res) {
                return 'false';
              }

              return res === true ? true : (getI18nMessage(t, res) as string);
            },
          }
        : undefined,
      disabled: disabledResolved,
    });

    const ref: React.RefCallback<any> = (el) => {
      field.ref(el);
      setFieldRef(name, el as FieldEl);

      if (el instanceof HTMLInputElement) {
        fieldSetterMap.set(el, onChange);
      }
    };

    const filterPipe = useMemo(() => options?.filters, [options?.filters]);

    // const onBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    //   const event = e.nativeEvent as InputEvent;

    //   const input = e.currentTarget;

    //   const start = input.selectionStart ?? 0;
    //   const end = input.selectionEnd ?? 0;

    //   const nextValue = input.value.substring(0, start) + event.data + input.value.substring(end);

    //   const filtered =
    //     (filterPipe ? filterPipe(nextValue, { isComposing: composingRef.current }) : nextValue) ??
    //     '';

    //   console.log('### filtered : ', filtered);

    //   if (filtered !== nextValue) {
    //     e.preventDefault();
    //     field.onChange(filtered);
    //   }
    // };

    /**
     *
     * @param e
     */
    const onChange = (e: any) => {
      if (disabledRef.current) {
        return;
      }

      if (readOnlyRef.current) {
        return;
      }

      const raw = String(e?.target?.value ?? '');
      const next =
        (filterPipe ? filterPipe(raw, { isComposing: composingRef.current }) : raw) ?? '';

      //   if (isSameOrParentPath(form.activeErrorField, String(name))) {
      //     form.setActiveErrorField('');
      //   }

      field.onChange(next);

      if (!runValidate) {
        return;
      }

      //   if (isSameOrParentPath(form.activeErrorField, String(name))) {
      const ctx: RuleContext<TValues> = {
        name: String(name),
        values: {
          ...form.getValues(),
          [name]: next,
        } as TValues,
        phase: 'change',
      };

      const result = runValidate(next, ctx);

      if (result === true) {
        form.clearErrors(name);
      }
    };
    // };

    const onCompositionStart = () => {
      composingRef.current = true;
    };

    const onCompositionEnd = (e: any) => {
      composingRef.current = false;

      const raw = String(e?.target?.value ?? '');
      const next = filterPipe ? filterPipe(raw, { isComposing: false }) : raw;

      if (isSameOrParentPath(form.activeErrorField, String(name))) {
        form.setActiveErrorField('');
      }

      field.onChange(next);
    };

    const onBlur = () => {
      field.onBlur();
      //moveNextField();
    };

    useEffect(() => {
      wasInvalidRef.current = !!fieldState.error;
    }, [fieldState.error]);

    useEffect(() => {
      readOnlyRef.current = readOnlyResolved;
    }, [readOnlyResolved]);

    useEffect(() => {
      disabledRef.current = disabledResolved;
    }, [disabledResolved]);

    useEffect(() => {
      visibleRef.current = visibleResolved;
    }, [visibleResolved]);

    // useEffect(() => {
    //   if (depNames.length > 0) {
    //     void form.trigger(name);
    //   }
    // }, [name, depNames.join('|'), JSON.stringify(depValues)]);

    return {
      field,
      fieldState,
      ref,
      inputProps: {
        ...field,
        disabled: disabledResolved,
        readOnly: readOnlyResolved,
        ref,
        value: field.value ?? '',
        onChange,
        onCompositionStart,
        onCompositionEnd,
      },
      value: field.value ?? '',
      onChangeValue: field.onChange,
      onBlur,
      visible: visibleResolved,
      disabled: disabledResolved,
      readOnly: readOnlyResolved,
    };
  };
}

/**
 *
 * @param useField
 * @returns
 */
export function createUseFields<TValues extends FieldValues>(useField: UseFieldFn<TValues>) {
  return function useFields(config: Partial<Record<Path<TValues>, UseFieldOptions<TValues>>>) {
    const result = {} as Record<Path<TValues>, ReturnType<UseFieldFn<TValues>>>;
    for (const name of Object.keys(config) as Array<Path<TValues>>) {
      result[name] = useField(name, config[name]);
    }
    return result;
  };
}
